import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeartIcon, DownloadIcon, GlobeIcon, User as UserIcon, TrophyIcon, FireIcon, EyeIcon, StarIcon } from '@phosphor-icons/react';
import { Pagination } from './Pagination';
import { supabase } from '../lib/supabase';
import { Generation, WeeklyChallenge } from '../types';
import { useAuth } from '../context/AuthContext';
import { HallOfFame } from './HallOfFame';
import { ChallengeSubmissions } from './ChallengeSubmissions';
import { ImageDetailModal } from './ImageDetailModal';

export const Discover: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Generation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeChallenge, setActiveChallenge] = useState<WeeklyChallenge | null>(null);
  const [activeFilter, setActiveFilter] = useState<'trending' | 'new' | 'top'>('trending');
  const [selectedImage, setSelectedImage] = useState<Generation | null>(null);

  useEffect(() => {
    fetchData();
  }, [page, activeFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Active Challenge (gracefully handle if table doesn't exist)
      try {
        const { data: challengeData, error: challengeError } = await supabase
          .from('weekly_challenges')
          .select('*')
          .eq('is_active', true)
          .maybeSingle();

        if (!challengeError && challengeData) setActiveChallenge(challengeData);
      } catch (e) {
        // Table may not exist yet, ignore
        console.log('Weekly challenges table not available yet');
      }

      // Fetch Public Generations with sorting based on filter
      const start = (page - 1) * 12;
      const end = start + 11;

      let query = supabase
        .from('generations')
        .select(`
          *,
          user:profiles (
            username,
            avatar_url
          )
        `)
        .eq('is_public', true);

      // Apply sorting based on active filter
      if (activeFilter === 'new') {
        query = query.order('created_at', { ascending: false });
      } else if (activeFilter === 'top') {
        query = query.order('likes_count', { ascending: false, nullsFirst: false });
      } else {
        // Trending: Order by a calculated score (likes + views weighted by recency)
        // For now, use likes_count as a proxy; could use trending_score if computed server-side
        query = query.order('likes_count', { ascending: false, nullsFirst: false })
          .order('views_count', { ascending: false, nullsFirst: false });
      }

      const { data, error } = await query.range(start, end);

      if (error) throw error;

      // Transform data to ensure user object structure
      const transformedData = (data || []).map((item: any) => ({
        ...item,
        user: Array.isArray(item.user) ? item.user[0] : item.user
      }));

      setItems(transformedData);
    } catch (err) {
      console.error('Error fetching discover feed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (id: string) => {
    if (!user) return; // Prompt login

    // Check if already liked
    const item = items.find(i => i.id === id);
    const isLiked = item?.is_liked;

    // Optimistic UI update
    setItems(prev => prev.map(item =>
      item.id === id ? {
        ...item,
        is_liked: !isLiked,
        likes_count: isLiked ? Math.max((item.likes_count || 1) - 1, 0) : (item.likes_count || 0) + 1
      } : item
    ));

    if (isLiked) {
      // Remove like
      const { error } = await supabase.from('likes').delete().eq('user_id', user.id).eq('generation_id', id);
      if (error) console.error('Error removing like:', error);
    } else {
      // Add like
      const { error } = await supabase.from('likes').insert({ user_id: user.id, generation_id: id });
      if (error) console.error('Error liking:', error);
    }
  };

  const handleFavorite = async (id: string) => {
    if (!user) return;

    const item = items.find(i => i.id === id);
    const isFavorited = item?.is_favorited;

    // Optimistic UI update
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, is_favorited: !isFavorited } : item
    ));

    if (isFavorited) {
      // Remove from favorites
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('generation_id', id);
      if (error) console.error('Error removing favorite:', error);
    } else {
      // Add to favorites
      const { error } = await supabase
        .from('favorites')
        .insert({ user_id: user.id, generation_id: id });
      if (error) console.error('Error favoriting:', error);
    }
  };

  return (
    <div className="flex-1 min-h-screen bg-onyx relative z-10 px-6 py-10 w-full overflow-y-auto">
      <div className="max-w-[1600px] mx-auto">

        {/* Weekly Challenge Banner */}
        {activeChallenge && (
          <>
            <div className="mb-12 relative rounded-2xl overflow-hidden bg-gradient-to-r from-indigo-900/40 to-purple-900/40 border border-indigo-500/20 p-8 flex items-center justify-between group">
              <div className="relative z-10 max-w-2xl">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-indigo-500 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-[0_0_15px_rgba(99,102,241,0.5)] animate-pulse">
                    Weekly Challenge
                  </span>
                  <span className="text-indigo-200 text-sm font-mono h-full flex items-center mt-0.5">Ends in 3 days</span>
                </div>
                <h2 className="text-4xl font-heading font-bold text-white mb-4 drop-shadow-xl">{activeChallenge.title}</h2>
                <p className="text-zinc-300 text-lg leading-relaxed max-w-xl">{activeChallenge.description}</p>

                <div className="mt-6 flex items-center gap-4">
                  <button
                    onClick={() => navigate('/scenes')}
                    className="px-6 py-3 bg-white text-black font-bold rounded-xl hover:bg-zinc-200 transition-colors shadow-xl shadow-white/10 flex items-center gap-2"
                  >
                    <TrophyIcon className="w-4 h-4" />
                    Join Challenge
                  </button>
                  <div className="flex -space-x-3 pl-2">
                    {/* Mock participants */}
                    {[1, 2, 3].map(i => (
                      <div key={i} className="w-10 h-10 rounded-full border-2 border-[#1a1a2e] bg-zinc-800" />
                    ))}
                    <div className="w-10 h-10 rounded-full border-2 border-[#1a1a2e] bg-zinc-700 flex items-center justify-center text-xs font-bold text-zinc-300">+42</div>
                  </div>
                </div>
              </div>

              {/* Decorative BG */}
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10 mix-blend-overlay" />
              <div className="absolute inset-0 bg-gradient-to-l from-black via-transparent to-transparent" />
            </div>

            <ChallengeSubmissions challengeId={activeChallenge.id} />
          </>
        )}

        {/* Hall of Fame */}
        <HallOfFame />

        {/* Filters & Header */}
        <div className="flex items-end justify-between mb-8 pb-6 border-b border-white/5">
          <div>
            <h1 className="text-3xl font-heading font-bold text-white mb-2 flex items-center gap-3">
              Discover <span className="text-zinc-600 text-lg font-normal">/ {activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}</span>
            </h1>
            <p className="text-zinc-500 text-sm">Explore the best generations from the community.</p>
          </div>

          <div className="flex gap-2">
            {(['trending', 'new', 'top'] as const).map(filter => (
              <button
                key={filter}
                onClick={() => { setActiveFilter(filter); setPage(1); }}
                className={`px-4 py-2 rounded-lg border text-sm transition-colors ${activeFilter === filter
                  ? 'bg-white text-black font-bold border-white'
                  : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="w-full h-96 flex items-center justify-center">
            <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
            {items.map((item) => (
              <div
                key={item.id}
                className="group break-inside-avoid relative mb-6 cursor-pointer"
                onClick={() => setSelectedImage(item)}
              >
                <div className="rounded-xl overflow-hidden relative border border-white/5 bg-zinc-900 shadow-2xl transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(79,70,229,0.15)] group-hover:border-indigo-500/30">
                  <img
                    src={item.image_url || item.img}
                    alt={item.prompt}
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">

                    {/* Top Actions */}
                    <div className="absolute top-4 right-4 translate-y-[-10px] opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 delay-75">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleFavorite(item.id); }}
                        className={`p-2 backdrop-blur-md rounded-lg border transition-colors ${item.is_favorited ? 'bg-yellow-500/80 border-yellow-400 text-white' : 'bg-zinc-900/60 border-white/10 text-white/70 hover:text-yellow-400 hover:bg-white/10'}`}
                        title={item.is_favorited ? "Remove from favorites" : "Add to favorites"}
                      >
                        <StarIcon className={`w-4 h-4 ${item.is_favorited ? 'fill-current' : ''}`} />
                      </button>
                    </div>

                    <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      {/* Creator Info */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-zinc-800 border border-white/10 overflow-hidden">
                          {item.user?.avatar_url ? (
                            <img src={item.user.avatar_url} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-500"><UserIcon className="w-4 h-4" /></div>
                          )}
                        </div>
                        <div>
                          <p className="text-white text-xs font-bold tracking-wide shadow-black drop-shadow-md cursor-pointer hover:underline">
                            @{item.user?.username || 'user'}
                          </p>
                          <p className="text-[10px] text-zinc-300/80 truncate max-w-[150px]">
                            {new Date(item.created_at || '').toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {/* Prompt Preview */}
                      {/* <p className="text-xs text-zinc-300 line-clamp-2 mb-4 opacity-0 group-hover:opacity-100 transition-opacity delay-100 duration-500">
                                                {item.prompt}
                                            </p> */}

                      {/* Actions Bar */}
                      <div className="flex justify-between items-center pt-3 border-t border-white/10">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleLike(item.id); }}
                            className="flex items-center gap-1.5 text-zinc-300 hover:text-red-400 transition-colors group/like"
                          >
                            <HeartIcon className={`w-4 h-4 ${item.is_liked ? 'fill-red-500 text-red-500' : 'group-hover/like:scale-110 transition-transform'}`} />
                            <span className="text-[10px] font-medium">{item.likes_count || 0}</span>
                          </button>
                          <div className="flex items-center gap-1.5 text-zinc-400">
                            <EyeIcon className="w-4 h-4" />
                            <span className="text-[10px] font-medium">{item.views_count || 0}</span>
                          </div>
                        </div>

                        <button className="p-1.5 hover:bg-white/10 rounded-full text-zinc-300 hover:text-white transition-colors">
                          <DownloadIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 flex justify-center">
          <Pagination currentPage={page} totalPages={10} onPageChange={setPage} />
        </div>
      </div>

      {/* Image Detail Modal */}
      {selectedImage && (
        <ImageDetailModal
          generation={selectedImage}
          user={user}
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          isLiked={!!selectedImage.is_liked}
          isFavorited={!!selectedImage.is_favorited}
          onToggleLike={(e) => {
            e.stopPropagation();
            handleLike(selectedImage.id);
            setSelectedImage(prev => prev ? ({
              ...prev,
              is_liked: !prev.is_liked,
              likes_count: prev.is_liked ? (prev.likes_count || 1) - 1 : (prev.likes_count || 0) + 1
            }) : null);
          }}
          onToggleFavorite={(e) => {
            e.stopPropagation();
            handleFavorite(selectedImage.id);
            setSelectedImage(prev => prev ? ({ ...prev, is_favorited: !prev.is_favorited }) : null);
          }}
          onTogglePublic={(e) => { e.stopPropagation() }}
        />
      )}
    </div>
  );
};